import { Money } from '@klear/money'
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import { useParams, useNavigate } from 'react-router'
import { Button } from '@klear/ui/components/button'
import { useTrader } from '@klear/ui/hooks/use-trader'
import { useKlearFinancialAccount } from '@klear/ui/hooks/use-klear-financial-account'
import { useTraderExternalBankAccounts } from '@klear/ui/hooks/use-trader-external-bank-accounts'
import { useBankTransferMutations } from '@klear/ui/hooks/use-bank-transfer'
import {
  inboundTransferFormToWire,
  outboundTransferFormToWire,
  transferFormHasErrors,
  validateInboundTransferForm,
  validateOutboundTransferForm,
  type InboundTransferForm,
  type OutboundTransferForm,
  type TransferFormErrorMap
} from '@klear/bank-transfer/wire.client'
import { ROUTE_EXTERNAL_BANK, ROUTE_KLEAR_ACCOUNT } from '@klear/constants'
import { PAYMENT_NETWORK_ACH, PAYMENT_NETWORK_WIRE, type BankPaymentNetwork } from '@klear/bank-account/constants'
import { DEFAULT_CURRENCY_CODE } from '@klear/constants/currency'
import { Label } from '@klear/ui/components/label'
import { Combobox } from '@klear/ui/components/combobox'
import { MoneyInput } from '@klear/ui/components/money-input'
import { Alert, AlertDescription, AlertTitle } from '@klear/ui/components/alert'
import { PaymentReferenceInput } from '@klear/ui/components/payment-reference-input'
import { RadioGroup, RadioGroupItem } from '@klear/ui/components/radio-group'
import { Card, CardContent, CardHeader, CardTitle } from '@klear/ui/components/card'
import { KlearAccountSelect } from '@klear/ui/components/klear-account-select'
import { Textarea } from '@klear/ui/components/textarea'
import toNumber from 'lodash/toNumber'
import values from 'lodash/values'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

type TransferDirection = 'to_klear' | 'from_klear'

function transferErrorList(errors: TransferFormErrorMap): string[] {
  return values(errors).filter((message): message is string => message != null && message.length > 0)
}

export default function MakeTransfer() {
  useDocumentTitle('Transfer Funds')
  const { traderId } = useParams()
  const navigate = useNavigate()
  const { urlPrefixApp } = useTrader(traderId)
  const { openAccounts, klearFinancialAccount, receivableAccount } = useKlearFinancialAccount()
  const [transferKlearAccountId, setTransferKlearAccountId] = useState<number | null>(null)
  const transferKlearAccount =
    openAccounts.find((a) => a.id === transferKlearAccountId) ??
    (klearFinancialAccount?.isOpen ? klearFinancialAccount : null) ??
    (receivableAccount?.isOpen ? receivableAccount : null) ??
    openAccounts[0] ??
    null

  useEffect(() => {
    if (transferKlearAccountId != null && openAccounts.some((a) => a.id === transferKlearAccountId)) return
    const defaultFromDashboard = klearFinancialAccount?.isOpen ? klearFinancialAccount.id : null
    const defaultFromReceivable = receivableAccount?.isOpen ? receivableAccount.id : null
    const defaultId = defaultFromDashboard ?? defaultFromReceivable ?? openAccounts[0]?.id ?? null
    if (defaultId != null) setTransferKlearAccountId(defaultId)
  }, [
    transferKlearAccountId,
    klearFinancialAccount?.id,
    klearFinancialAccount?.isOpen,
    receivableAccount?.id,
    receivableAccount?.isOpen,
    openAccounts
  ])

  const { traderExternalBankAccounts: externalBankAccounts } = useTraderExternalBankAccounts()
  const { createInboundTransfer, createOutboundTransfer, isCreating } = useBankTransferMutations()
  const [direction, setDirection] = useState<TransferDirection>('from_klear')
  const [externalBankAccountId, setExternalBankAccountId] = useState('')
  const [network, setNetwork] = useState<BankPaymentNetwork | ''>('')
  const [amount, setAmount] = useState<Money>(Money.zero(DEFAULT_CURRENCY_CODE))
  const [statementDescriptor, setStatementDescriptor] = useState('')
  const [note, setNote] = useState('')

  const verifiedAccounts = useMemo(
    () => externalBankAccounts.filter((account) => account.isReadyForTransfer),
    [externalBankAccounts]
  )

  const isOutbound = direction === 'from_klear'

  const selectedAccount = useMemo(
    () => verifiedAccounts.find((a) => a.id != null && a.id.toString() === externalBankAccountId),
    [externalBankAccountId, verifiedAccounts]
  )

  const availableNetworks = useMemo(
    () =>
      isOutbound && selectedAccount
        ? selectedAccount.supportedPaymentNetworks.filter(
            (n) => n === PAYMENT_NETWORK_ACH || n === PAYMENT_NETWORK_WIRE
          )
        : [],
    [isOutbound, selectedAccount]
  )

  const inboundForm = useMemo(
    (): InboundTransferForm => ({
      traderId: toNumber(traderId),
      external_bank_account_id: toNumber(externalBankAccountId) || 0,
      amount,
      statement_descriptor: statementDescriptor || undefined,
      klear_account_id: transferKlearAccount?.id,
      note: note || undefined
    }),
    [traderId, amount, externalBankAccountId, statementDescriptor, transferKlearAccount?.id, note]
  )

  const outboundForm = useMemo(
    (): OutboundTransferForm => ({
      ...inboundForm,
      network: network as BankPaymentNetwork
    }),
    [inboundForm, network]
  )

  const availableBalance = transferKlearAccount?.account ? transferKlearAccount.balanceAvailable : undefined
  const availableBalanceLabel = (availableBalance ?? Money.zero()).toString()

  const balanceCap = isOutbound ? availableBalance : undefined
  const balanceCapsThisTransfer = balanceCap != null && amount.currency === balanceCap.currency
  const amountHelperText = !isOutbound
    ? undefined
    : availableBalance == null || balanceCapsThisTransfer
      ? `Klear account available balance: ${availableBalanceLabel}`
      : `Klear account available balance: ${availableBalanceLabel} — this transfer is in ${amount.currency}, so the balance does not cap the amount`

  const showValidation = Boolean(externalBankAccountId && (!isOutbound || network))
  const formErrors = showValidation
    ? isOutbound
      ? validateOutboundTransferForm(outboundForm)
      : validateInboundTransferForm(inboundForm)
    : {}
  const errorList = transferErrorList(formErrors)

  const preferredNetwork = useCallback((supportedNetworks: BankPaymentNetwork[]) => {
    if (supportedNetworks.includes(PAYMENT_NETWORK_WIRE)) return PAYMENT_NETWORK_WIRE
    if (supportedNetworks.includes(PAYMENT_NETWORK_ACH)) return PAYMENT_NETWORK_ACH
    return '' as BankPaymentNetwork | ''
  }, [])

  const resetForm = useCallback(() => {
    setExternalBankAccountId('')
    setNetwork('')
    setAmount(Money.zero(DEFAULT_CURRENCY_CODE))
    setStatementDescriptor('')
  }, [])

  const handleDirectionChange = useCallback(
    (value: TransferDirection) => {
      setDirection(value)
      resetForm()
    },
    [resetForm]
  )

  const handleBankSelect = useCallback(
    (value: string | number | '') => {
      const accountId = value === '' ? '' : value.toString()
      setExternalBankAccountId(accountId)

      if (isOutbound) {
        const account = verifiedAccounts.find((a) => a.id != null && a.id.toString() === accountId)
        if (account) {
          setNetwork(preferredNetwork(account.supportedPaymentNetworks))
        } else {
          setNetwork('')
        }
      }
    },
    [isOutbound, preferredNetwork, verifiedAccounts]
  )

  const handleNetworkChange = useCallback((value: BankPaymentNetwork | '') => {
    setNetwork(value)
  }, [])

  async function handleFormSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!externalBankAccountId || (isOutbound && !network)) return
    if (openAccounts.length > 1 && transferKlearAccount?.id == null) return

    const errors = isOutbound ? validateOutboundTransferForm(outboundForm) : validateInboundTransferForm(inboundForm)
    if (transferFormHasErrors(errors)) return

    try {
      if (isOutbound) {
        await createOutboundTransfer(outboundTransferFormToWire(outboundForm))
      } else {
        await createInboundTransfer(inboundTransferFormToWire(inboundForm))
      }

      resetForm()
      navigate(`${urlPrefixApp}/${ROUTE_KLEAR_ACCOUNT}`)
    } catch (error: unknown) {
      console.error('Transfer error:', error)
    }
  }

  const navigateToExternalAccounts = useCallback(() => {
    navigate(`${urlPrefixApp}/${ROUTE_EXTERNAL_BANK}`)
  }, [navigate, urlPrefixApp])

  const bankAccountOptions = verifiedAccounts
    .filter((account) => account.id != null)
    .filter((account) => {
      if (direction === 'from_klear' && network) {
        return account.supportedPaymentNetworks.includes(network)
      }
      return true
    })
    .map((account) => ({
      label: `${account.bankName} ••••${account.last4} - ${account.accountHolderName}`,
      value: account.id!
    }))

  return (
    <section className="h-full flex-1 flex-col space-y-8 p-8 md:flex">
      <div className="flex items-center justify-between space-y-2">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Transfer Funds</h2>
        </div>
        <Button type="button" variant="outline" onClick={navigateToExternalAccounts}>
          Manage Bank Accounts
        </Button>
      </div>
      <form onSubmit={handleFormSubmit} className="flex flex-col gap-8">
        <div className="h-full flex flex-col gap-6">
          <KlearAccountSelect
            accounts={openAccounts}
            value={transferKlearAccount?.id}
            onChange={setTransferKlearAccountId}
            label="Klear Account"
          />

          <div className="space-y-4">
            <div className="font-semibold text-lg">Transfer Direction</div>
            <RadioGroup
              value={direction}
              onValueChange={(value) => handleDirectionChange(value as TransferDirection)}
              className="flex flex-row space-x-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="to_klear" id="to_klear" />
                <Label htmlFor="to_klear" className="cursor-pointer">
                  To Klear
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="from_klear" id="from_klear" />
                <Label htmlFor="from_klear" className="cursor-pointer">
                  From Klear
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-4">
            <div className="font-semibold text-lg">Accounts</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">From</CardTitle>
                </CardHeader>
                <CardContent>
                  {direction === 'from_klear' ? (
                    <div className="space-y-2">
                      <div className="font-semibold">Klear Account</div>
                      <div className="text-sm text-muted-foreground">
                        <div>Available Balance: {availableBalanceLabel}</div>
                        {transferKlearAccount && (
                          <div className="mt-1">
                            <div>Bank: {transferKlearAccount.bankName}</div>
                            {transferKlearAccount.accountNumber && (
                              <div>Account: ••••{transferKlearAccount.accountNumber.slice(-4)}</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label>External Bank Account</Label>
                      <Combobox
                        value={externalBankAccountId ? toNumber(externalBankAccountId) : ''}
                        valueType="number"
                        options={bankAccountOptions}
                        onChange={handleBankSelect}
                        error={
                          formErrors.external_bank_account_id
                            ? { message: formErrors.external_bank_account_id }
                            : undefined
                        }
                      />
                      {selectedAccount && (
                        <div className="text-sm text-muted-foreground mt-2">
                          <div>Account Holder: {selectedAccount.accountHolderName}</div>
                          <div>Account Type: {selectedAccount.accountType}</div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">To</CardTitle>
                </CardHeader>
                <CardContent>
                  {direction === 'to_klear' ? (
                    <div className="space-y-2">
                      <div className="font-semibold">Klear Account</div>
                      <div className="text-sm text-muted-foreground">
                        <div>Available Balance: {availableBalanceLabel}</div>
                        {transferKlearAccount && (
                          <div className="mt-1">
                            <div>Bank: {transferKlearAccount.bankName}</div>
                            {transferKlearAccount.accountNumber && (
                              <div>Account: ••••{transferKlearAccount.accountNumber.slice(-4)}</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label>External Bank Account</Label>
                      <Combobox
                        value={externalBankAccountId ? toNumber(externalBankAccountId) : ''}
                        valueType="number"
                        options={bankAccountOptions}
                        onChange={handleBankSelect}
                        error={
                          formErrors.external_bank_account_id
                            ? { message: formErrors.external_bank_account_id }
                            : undefined
                        }
                      />
                      {selectedAccount && (
                        <div className="text-sm text-muted-foreground mt-2">
                          <div>Account Holder: {selectedAccount.accountHolderName}</div>
                          <div>Account Type: {selectedAccount.accountType}</div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {direction === 'from_klear' && externalBankAccountId && availableNetworks.length > 0 && (
            <div className="space-y-4">
              <div className="font-semibold text-lg">Payment Network</div>
              <div className="space-y-2">
                <Label>Network</Label>
                <Combobox
                  value={network}
                  options={availableNetworks.map((networkOption) => ({
                    label: networkOption === PAYMENT_NETWORK_WIRE ? 'Wire US Domestic' : 'ACH',
                    value: networkOption
                  }))}
                  onChange={(value) => handleNetworkChange(value as BankPaymentNetwork | '')}
                  error={formErrors.network ? { message: formErrors.network } : undefined}
                />
              </div>
            </div>
          )}

          {externalBankAccountId && (direction === 'to_klear' || network) && (
            <div className="space-y-4">
              <div className="font-semibold text-lg">Transfer Details</div>
              <MoneyInput
                label="Amount"
                value={amount}
                onChange={setAmount}
                helperText={amountHelperText}
                errorMessage={formErrors.amount}
                max={balanceCap}
              />
              <PaymentReferenceInput
                network={direction === 'from_klear' ? (network as BankPaymentNetwork) : PAYMENT_NETWORK_ACH}
                value={statementDescriptor}
                onChange={setStatementDescriptor}
                error={formErrors.statement_descriptor}
              />
              {!isOutbound && (
                <div className="space-y-2">
                  <Label htmlFor="transfer_note">Note</Label>
                  <Textarea
                    id="transfer_note"
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    maxLength={500}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        {errorList.length > 0 && (
          <Alert variant="destructive" className="mb-4 shrink-0 max-h-[20%] overflow-y-auto">
            <AlertTitle>There are errors in your submission</AlertTitle>
            <AlertDescription>
              <ul className="list-disc pl-5">
                {errorList.map((msg, idx) => (
                  <li key={idx}>{msg}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
        <div className="flex justify-end mt-5">
          <Button
            type="submit"
            disabled={isCreating || !externalBankAccountId || (direction === 'from_klear' && !network)}
          >
            {isCreating ? 'Processing...' : 'Make Transfer'}
          </Button>
        </div>
      </form>
    </section>
  )
}
